// frontend/src/pages/support/SupportPage.js
import React, { useState } from 'react';
import { Mail, Phone, MessageCircle, MapPin, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import useAuthStore from '../../store/authStore';

const SupportPage = () => {
  const { user } = useAuthStore();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleEmailSupport = () => {
    const userEmail = user?.email || '';
    const subject = encodeURIComponent('Fastel Support Request');
    const body = encodeURIComponent(
      `Hello Fastel Support Team,\n\n` +
      `I need assistance with the following:\n\n` +
      `Name: ${user?.firstName || ''} ${user?.lastName || ''}\n` +
      `Email: ${userEmail}\n` +
      `Phone: ${user?.phone || ''}\n\n` +
      `Description of issue:\n\n` +
      `\n\n` +
      `Thank you,\n` +
      `${user?.firstName || ''} ${user?.lastName || ''}`
    );
    
    window.location.href = `mailto:fasteltelecomltd@gmail.com?subject=${subject}&body=${body}`;
  };

  const handleCallSupport = () => {
    window.location.href = 'tel:+2348109650254';
  };

  const handleCallSupport2 = () => {
    window.location.href = 'tel:+2349042183619';
  };

  const handleWhatsAppSupport = () => {
    const message = encodeURIComponent(
      `Hello Fastel Support! I need assistance with my account.\n\n` +
      `Name: ${user?.firstName || ''} ${user?.lastName || ''}\n` +
      `Email: ${user?.email || ''}`
    );
    window.open(`https://wa.me/2348109650254?text=${message}`, '_blank');
  };

  const handleWhatsAppSupport2 = () => {
    const message = encodeURIComponent(
      `Hello Fastel Support! I need assistance with my account.\n\n` +
      `Name: ${user?.firstName || ''} ${user?.lastName || ''}\n` +
      `Email: ${user?.email || ''}`
    );
    window.open(`https://wa.me/2349042183619?text=${message}`, '_blank');
  };

  const supportOptions = [
    {
      id: 'email',
      icon: Mail,
      label: 'Email Support',
      description: 'fasteltelecomltd@gmail.com',
      subDescription: '24/7 support, replies within 1 hour',
      color: '#2563EB',
      bg: '#EFF6FF',
      onPress: handleEmailSupport,
    },
    {
      id: 'phone',
      icon: Phone,
      label: 'Phone Support',
      description: '08109650254',
      subDescription: 'Mon-Sat, 9am - 6pm',
      color: '#059669',
      bg: '#ECFDF5',
      onPress: handleCallSupport,
    },
    {
      id: 'phone2',
      icon: Phone,
      label: 'Phone Support 2',
      description: '09042183619',
      subDescription: 'Mon-Sat, 9am - 6pm',
      color: '#059669',
      bg: '#ECFDF5',
      onPress: handleCallSupport2,
    },
    {
      id: 'whatsapp',
      icon: MessageCircle,
      label: 'WhatsApp',
      description: '08109650254',
      subDescription: 'Chat us on WhatsApp',
      color: '#25D366',
      bg: '#ECFDF5',
      onPress: handleWhatsAppSupport,
    },
    {
      id: 'whatsapp2',
      icon: MessageCircle,
      label: 'WhatsApp 2',
      description: '09042183619',
      subDescription: 'Chat us on WhatsApp',
      color: '#25D366',
      bg: '#ECFDF5',
      onPress: handleWhatsAppSupport2,
    },
    {
      id: 'address',
      icon: MapPin,
      label: 'Address',
      description: 'NO. 35, MIJINYAWA STREET TUDUN JUKUN',
      subDescription: 'ZARIA, KADUNA STATE, NIGERIA',
      color: '#8B5CF6',
      bg: '#F5F3FF',
      onPress: null,
    },
  ];

  const faqs = [
    {
      id: '1',
      question: 'How do I fund my wallet?',
      answer: 'Go to the Wallet tab, tap "Fund Wallet", enter amount, and complete payment via Monnify.',
    },
    {
      id: '2',
      question: 'How do I buy airtime/data?',
      answer: 'Go to Services tab, select Airtime or Data, enter phone number, choose amount, and confirm purchase.',
    },
    {
      id: '3',
      question: 'What if my transaction fails?',
      answer: 'If a transaction fails, the amount will be refunded to your wallet automatically within 24 hours. Contact support if not refunded.',
    },
    {
      id: '4',
      question: 'How do I list my business?',
      answer: 'Go to Marketplace tab, tap "Create Business", fill in your business details, and submit for approval.',
    },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <p className="text-sm text-gray-600 leading-relaxed">
          Need help? Reach out to us through any of these channels:
        </p>
      </div>

      {/* Quick Support Options */}
      <div className="space-y-3 mb-6">
        {supportOptions.map((option) => (
          <div
            key={option.id}
            onClick={option.onPress}
            className={`bg-white rounded-2xl p-4 shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-start cursor-pointer ${!option.onPress && 'cursor-default'}`}
          >
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1"
              style={{ backgroundColor: option.bg }}
            >
              <option.icon size={20} color={option.color} />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-800 mb-0.5">
                {option.label}
              </h4>
              <p className="text-sm text-gray-600">
                {option.description}
              </p>
              {option.subDescription && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {option.subDescription}
                </p>
              )}
            </div>
            {option.onPress && (
              <ChevronDown className="h-4 w-4 text-gray-400 mt-2 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>

      {/* Support Team Message */}
      <div className="bg-blue-50 rounded-2xl p-4 mb-6 border border-blue-100">
        <p className="text-xs text-gray-600 text-center leading-relaxed">
          Our support team is always ready to help you with any questions or issues you may have.
        </p>
      </div>

      {/* FAQs */}
      <div className="bg-white rounded-2xl shadow-neomorph p-6">
        <h3 className="text-base font-bold text-gray-800 mb-4">
          Frequently Asked Questions
        </h3>
        <div className="space-y-1">
          {faqs.map((faq) => (
            <div key={faq.id} className="border-b border-gray-100 last:border-0">
              <button
                onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                className="w-full flex justify-between items-center py-3 px-1 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <span className="text-sm font-medium text-gray-800 text-left flex-1">
                  {faq.question}
                </span>
                {expandedFaq === faq.id ? (
                  <ChevronUp className="h-4 w-4 text-gray-500 flex-shrink-0 ml-2" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0 ml-2" />
                )}
              </button>
              {expandedFaq === faq.id && (
                <div className="pb-3 px-1">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* User Info */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-400">
          Logged in as: {user?.email || 'Not logged in'}
        </p>
      </div>
    </div>
  );
};

export default SupportPage;